import { useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { Status } from '@beyond-js/ui/react';
import { Condition, type Capture, type Standing } from '@snapshots/domain';
import { Address, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import { marks } from './entries';

/**
 * The one status of a snapshot (D42), drawn the same in the list and in its header: Pending, Completed,
 * Failed, Cancelled, or Deleted (with the date it stays recoverable until, where there is room for it).
 */
export function SnapshotStatus({ capture, short = false }: { capture: Capture; short?: boolean }) {
	const { text, date } = usePresence();
	const standing: Standing = Condition.of(capture);
	const label = standing === 'deleted' && !short ? text('capture.standing.deleted', { until: date(capture.recoverable) }) : text(`capture.standing.${standing}`);
	return (
		<span data-capture-state={capture.state} data-standing={standing}>
			<Status state={marks[standing]} label={label} />
		</span>
	);
}

/** A moment as "3 minutes ago", with the whole date on hover and for assistive technology */
export function Moment({ at }: { at?: string }) {
	const { relative, date } = usePresence();
	if (!at) return <>—</>;
	return (
		<time dateTime={at} title={date(at)}>
			{relative(at)}
		</time>
	);
}

/** A row's project or organization by name, leading to the list narrowed to it */
export function Scope(place: { project?: string; organization?: string }) {
	const { names } = useFrame();
	const { text } = usePresence();
	const id = place.project ?? place.organization ?? '';
	// A cell is drawn once, so it follows names learned after it
	const name = useSyncExternalStore(names.listen, () => names.name(id));
	return (
		<Link to={Address.of(place)} className="text-ink underline" data-place={id}>
			{name ?? (place.project ? text('nav.project.unknown') : id)}
		</Link>
	);
}
